// @flow
import React, {useState, useEffect, useCallback} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  Typography,
  Hidden,
  Divider,
  ListItem,
  useMediaQuery,
  List,
  MenuItem,
} from '@material-ui/core';
import {useLocation} from 'react-router-dom';
import Skeleton from '@material-ui/lab/Skeleton';
import {colors} from '../../../../../assets/styles/colors';
import ReviewItem from '../ReviewItem/ReviewItem';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import Pagination from '@material-ui/lab/Pagination';

import type {
  Pagination as PaginationType,
  Order,
  Store,
  Currency,
} from '../../../../../entities';

type Props = {
  fetchReviews: ({queryFilter?: {[key: string]: string}}) => void,
  isFetchingReviews?: boolean,
  pagination?: PaginationType,
  store: Store,
  reviews?: Order[],
  currency?: Currency,
  tierRestricted?: boolean,
};

const useStyles = makeStyles(theme => ({
  listItem: {
    backgroundColor: 'transparent',
    padding: 0,
  },
  sizeSelect: {
    margin: theme.spacing(2, 0),
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  wrapReviews: {
    background: '#F8F9FB',
    boxShadow: '0px',
    borderRadius: '0px',
    [theme.breakpoints.up('md')]: {
      background: 'white',
      borderRadius: '12px',
    },
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    backgroundColor: colors.grey200,
  },
  headerColumn: {
    flex: '2 1',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  divider: {
    margin: theme.spacing(0, 3),
    background: colors.grey200,
  },
  reviewsList: {
    paddingTop: 0,
    padding: '16px',
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}));

export default function PendingReviewsList(props: Props) {
  const {
    pagination,
    reviews,
    fetchReviews,
    store,
    isFetchingReviews,
    currency,
    tierRestricted,
  } = props;
  const location = useLocation();
  const classes = useStyles();

  const getSearchFromQuery = (search: string) => {
    const s = queryString.parse(search);
    return s;
  };
  const query = getSearchFromQuery(location.search);
  const p = parseInt(query.page, 10);
  const s = parseInt(query.size, 10);
  const [page, setPage] = useState(isNaN(p) ? 1 : p);
  const [size, setSize] = useState(isNaN(s) ? 16 : s);

  const buildQueryParamsCallback = useCallback(() => {
    const params: {[key: string]: any} = {
      size: size,
      page: page - 1,
    };
    const queryString = getSearchFromQuery(location.search);
    if (queryString) {
      params.name = queryString.name;
    }
    return params;
  }, [location, page, size]);

  useEffect(() => {
    const queryFilter = buildQueryParamsCallback();
    if (queryFilter) {
      fetchReviews({queryFilter});
    }
  }, [buildQueryParamsCallback, fetchReviews, location]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSizeChange = event => {
    if (event) {
      setSize(event.target.value);
    }
  };

  const paginationArea = (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <option value={8}>8 por página</option>
          <option value={16}>16 por página</option>
          <option value={24}>24 por página</option>
          <option value={32}>32 por página</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <MenuItem value={8}>8 por página</MenuItem>
          <MenuItem value={16}>16 por página</MenuItem>
          <MenuItem value={24}>24 por página</MenuItem>
          <MenuItem value={32}>32 por página</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );
  const tablet = useMediaQuery('(max-width:960px)');

  return (
    <div className={classes.wrapReviews}>
      <div>
        <Hidden smDown>
          <div className={classes.tableHeader}>
            <Typography className={classes.headerColumn}>
              Número de orden
            </Typography>
            <Typography className={classes.headerColumn}>Fecha</Typography>
            <Typography className={classes.headerColumn}>
              Nombre del Cliente
            </Typography>
            <Typography className={classes.headerColumn}>{`Monto ${
              currency ? currency.symbol : 'S/.'
            }`}</Typography>
            <Typography className={classes.headerColumn}></Typography>
            <Typography className={classes.headerColumn}></Typography>
            <div style={{width: 20}} />
          </div>
        </Hidden>
        <List className={classes.reviewsList}>
          {isFetchingReviews && !reviews
            ? Array.from({length: size}, (x, i) => (
                <ListItem key={`skelly-${i}`}>
                  <Skeleton
                    variant="rect"
                    height={tablet ? '128px' : '48px'}
                    width="100%"
                  />
                </ListItem>
              ))
            : reviews &&
              reviews.map((review: Order, index: number) => {
                return (
                  <div key={`pending-review-item-${index}`}>
                    <ListItem className={classes.listItem}>
                      <ReviewItem
                        review={review}
                        store={store}
                        tierRestricted={tierRestricted}
                      />
                    </ListItem>
                    <Hidden smDown>
                      <Divider className={classes.divider} />
                    </Hidden>
                  </div>
                );
              })}
        </List>
        {paginationArea}
      </div>
    </div>
  );
}

PendingReviewsList.propTypes = {
  fetchReviews: PropTypes.func.isRequired,
  isFetchingReviews: PropTypes.bool,
  pagination: PropTypes.object,
  pendingReviews: PropTypes.array,
  store: PropTypes.object.isRequired,
  currency: PropTypes.object,
  tierRestricted: PropTypes.bool,
};
